import React, { useState, useEffect } from "react";
import { ThinTextField, AddButton } from "../../common/GeneralComponent";
import { toast } from "react-toastify";
import { fetchData } from "../../../utils/functions";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Plus, Trash } from "lucide-react";



const FeatureDesign = ({
  featureCategoryId,
  onDesignSaved,
  onNext,
  isEditMode,
  showNextButton,
  showUpdateOnly,
  showSubmitAndNext,
  stepCompleted,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [hasDesign, setHasDesign] = useState(false);
  const [designs, setDesigns] = useState([
    {
      id: null,
      design: "",
      image_2d: null,
      boq_file: null,
      imagePreview: null,
      boqName: null,
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (isEditMode && featureCategoryId) {
      fetchDesigns();
    }
  }, [featureCategoryId, isEditMode]);

  const fetchDesigns = async () => {
    try {
      const response = await fetchData(
        `feature-designs/${featureCategoryId}`,
        "GET",
        null,
        {},
        {},
        navigate,
        "",
        dispatch
      );

      if (response?.data && response.data.length > 0) {
        setHasDesign(true);
        setDesigns(
          response.data.map((design) => ({
            id: design.feature_category_designs_id,
            design: design.design_name || "",
            image_2d: null,
            boq_file: null,
            imagePreview: null,
            boqName: design.boq_file ? design.boq_file.split("/").pop() : null,
            existing_image: design.image_2d,
            existing_boq: design.boq_file,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching designs:", error);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";

    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }

    if (imagePath.startsWith("/uploads")) {
      return `${process.env.REACT_APP_BASE_URL || "http://localhost:8000"}${imagePath}`;
    }

    return `${process.env.REACT_APP_BASE_URL || "http://localhost:8000"}/uploads/feature_designs/${imagePath}`;
  };

  const getDisplayName = (imagePath) => {
    if (!imagePath) return "";
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath.split("/").pop();
    }
    return imagePath;
  };

  const handleImageClick = (imagePath, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (imagePath) {
      setSelectedImage(imagePath);
      setShowImageModal(true);
    }
  };

  const closeImageModal = (e) => {
    e?.stopPropagation();
    setShowImageModal(false);
    setSelectedImage(null);
  };

  const handleAddDesign = () => {
    setDesigns([
      ...designs,
      {
        id: null,
        design: "",
        image_2d: null,
        boq_file: null,
        imagePreview: null,
        boqName: null,
      },
    ]);
  };

  const handleRemoveDesign = (index) => {
    if (designs.length > 1) {
      setDesigns(designs.filter((_, i) => i !== index));
    }
  };

  const handleDesignChange = (index, field, value) => {
    const updatedDesigns = [...designs];
    updatedDesigns[index][field] = value;
    setDesigns(updatedDesigns);
  };

  const handleImageChange = (index, file) => {
    if (file) {
      if (file.type !== "image/png") {
        toast.error("Only PNG images are allowed");
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Image must be less than 2MB");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const updatedDesigns = [...designs];
        updatedDesigns[index].image_2d = file;
        updatedDesigns[index].imagePreview = reader.result;
        setDesigns(updatedDesigns);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBoqChange = (index, file) => {
    if (file) {
      if (file.type !== "application/pdf") {
        toast.error("Only PDF files are allowed for BOQ");
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast.error("BOQ file must be less than 2MB");
        return;
      }

      const updatedDesigns = [...designs];
      updatedDesigns[index].boq_file = file;
      updatedDesigns[index].boqName = file.name;
      setDesigns(updatedDesigns);
    }
  };

  const validateDesigns = () => {
    if (!hasDesign) {
      return true; // Skip validation if no design
    }

    for (let i = 0; i < designs.length; i++) {
      if (!designs[i].design.trim()) {
        toast.error(`Design name is required for design ${i + 1}`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateDesigns()) {
      return;
    }

    try {
      setLoading(true);

      if (hasDesign) {
        for (const design of designs) {
          const formData = new FormData();
          formData.append("feature_category_id", featureCategoryId);
          formData.append("design_name", design.design);

          if (design.image_2d) {
            formData.append("image_2d", design.image_2d);
          }
          if (design.boq_file) {
            formData.append("boq_file", design.boq_file);
          }

          if (isEditMode && design.id) {
            // Update existing design
            await fetchData(
              `feature-design-update/${design.id}`,
              "PUT",
              formData,
              { "Content-Type": "multipart/form-data" },
              {},
              navigate,
              "",
              dispatch
            );
          } else {
            // Create new design
            await fetchData(
              "feature-design-create",
              "POST",
              formData,
              { "Content-Type": "multipart/form-data" },
              {},
              navigate,
              "",
              dispatch
            );
          }
        }
      }

      if (isEditMode) {
        toast.success("Feature designs updated successfully");
        fetchDesigns();
      } else {
        toast.success("Feature designs saved successfully");
        if (onDesignSaved) onDesignSaved();
      }
    } catch (error) {
      console.error("Error saving designs:", error);
      toast.error(
        "Failed to save designs. Please check the console for details."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (stepCompleted && onNext) {
      onNext();
    } else {
      toast.error(
        "Please submit the designs first before proceeding to the next step"
      );
    }
  };

  return (
    <div className="space-y-4">
      {/* Has Design Toggle */}
      <div className="flex items-center space-x-3 mb-4">
        <label className="text-[0.9rem] text-[#565656] font-[InterLight]">
          Has Design
        </label>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={hasDesign}
            onChange={(e) => setHasDesign(e.target.checked)}
            className="sr-only peer"
          />
          <div
            className="w-11 h-6 
            bg-[#e2e8f0]
            peer-focus:outline-none 
            rounded-full 
            peer 
            peer-checked:after:translate-x-full 
            peer-checked:after:border-white 
            after:content-[''] 
            after:absolute 
            after:top-[2px] 
            after:left-[2px] 
            after:bg-white 
            after:rounded-full 
            after:h-5 
            after:w-5 
            after:transition-all 
            peer-checked:bg-[#137e81]
            border border-[#b0b0b0]"
          ></div>
        </label>
      </div>

      {hasDesign && (
        <>
          {designs.map((design, index) => (
            <div
              key={index}
              className="border border-gray-200 rounded-lg p-4 space-y-4"
            >
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-gray-700">
                  Design {index + 1}
                </h3>
                {designs.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveDesign(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash size={20} />
                  </button>
                )}
              </div>

              {isEditMode ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <ThinTextField
                        label="Design Name"
                        value={design.design}
                        onChange={(e) =>
                          handleDesignChange(index, "design", e.target.value)
                        }
                        placeholder="Enter design name"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Current 2D Image
                      </label>
                      {design.existing_image ? (
                        <div className="border border-gray-300 rounded-md p-4 bg-gray-50 h-[42px] flex items-center">
                          <button
                            type="button"
                            className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 p-1 rounded-md transition-all duration-200 border border-transparent hover:border-blue-200 w-full text-left"
                            onClick={(e) =>
                              handleImageClick(design.existing_image, e)
                            }
                            title="Click to view image"
                          >
                            <i className="fa fa-image text-blue-500 text-sm"></i>
                            <span className="text-sm text-blue-600 hover:text-blue-800 font-medium truncate">
                              View: {getDisplayName(design.existing_image)}
                            </span>
                            <i className="fa fa-eye text-sm text-blue-400 ml-auto"></i>
                          </button>
                        </div>
                      ) : (
                        <div className="border border-gray-300 rounded-md p-4 bg-gray-50 h-[42px] flex items-center justify-center text-sm text-gray-500">
                          No current image
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        {design.existing_image
                          ? "Replace 2D Image"
                          : "Upload 2D Image"}{" "}
                        (PNG, max 2MB)
                      </label>
                      <input
                        type="file"
                        accept=".png"
                        onChange={(e) =>
                          handleImageChange(index, e.target.files[0])
                        }
                        className="text-[15px] font-[InterLight] text-[#565656] bg-[#ebeef2] focus:bg-[#dde2e9] py-2 px-4 outline-none border-[#dde2e9] border rounded-[0.35rem] w-full h-[42px]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-2">
                        Current BOQ File
                      </label>
                      {design.existing_boq ? (
                        <div className="border border-gray-300 rounded-md p-4 bg-gray-50 h-[42px] flex items-center">
                          <div className="flex items-center gap-2">
                            <i className="fa fa-file-pdf text-red-500 text-sm"></i>
                            <span className="text-sm text-gray-600 truncate">
                              {design.existing_boq.split("/").pop()}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="border border-gray-300 rounded-md p-4 bg-gray-50 h-[42px] flex items-center justify-center text-sm text-gray-500">
                          No current BOQ file
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-2">
                        {design.existing_boq ? "Replace BOQ" : "Upload BOQ"}{" "}
                        (PDF, max 2MB)
                      </label>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={(e) =>
                          handleBoqChange(index, e.target.files[0])
                        }
                        className="text-[15px] font-[InterLight] text-[#565656] bg-[#ebeef2] focus:bg-[#dde2e9] py-2 px-4 outline-none border-[#dde2e9] border rounded-[0.35rem] w-full h-[42px]"
                      />
                    </div>
                    {design.imagePreview && (
                      <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700 mb-2">
                          New Image Preview
                        </label>
                        <div className="border border-gray-300 rounded-md bg-gray-50 h-[42px] flex items-center px-4">
                          <button
                            type="button"
                            className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 p-1 rounded-md transition-all duration-200 border border-transparent hover:border-blue-200 w-full text-left"
                            onClick={(e) =>
                              handleImageClick(design.imagePreview, e)
                            }
                            title="Click to view preview"
                          >
                            <i className="fa fa-image text-green-500 text-sm"></i>
                            <span className="text-sm text-green-600 hover:text-green-800 font-medium">
                              Preview New Image
                            </span>
                            <i className="fa fa-eye text-sm text-green-400 ml-auto"></i>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <ThinTextField
                        label="Design Name"
                        value={design.design}
                        onChange={(e) =>
                          handleDesignChange(index, "design", e.target.value)
                        }
                        placeholder="Enter design name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray mt-2 mb-1 block">
                        {design.existing_image
                          ? "Replace 2D Image"
                          : "Upload 2D Image"}{" "}
                        (PNG, max 2MB)
                      </label>
                      <input
                        type="file"
                        accept=".png"
                        onChange={(e) =>
                          handleImageChange(index, e.target.files[0])
                        }
                        className="text-[15px] font-[InterLight] text-[#565656] bg-[#ebeef2] focus:bg-[#dde2e9] py-2 px-4 outline-none border-[#dde2e9] border rounded-[0.35rem] w-full"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray mt-2 mb-1 block">
                        {design.existing_boq ? "Replace BOQ" : "Upload BOQ"}{" "}
                        (PDF, max 2MB)
                      </label>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={(e) =>
                          handleBoqChange(index, e.target.files[0])
                        }
                        className="text-[15px] font-[InterLight] text-[#565656] bg-[#ebeef2] focus:bg-[#dde2e9] py-2 px-4 outline-none border-[#dde2e9] border rounded-[0.35rem] w-full"
                      />
                    </div>
                  </div>
                  {(design.existing_image ||
                    design.imagePreview ||
                    design.boqName) && (
                    <div className="grid grid-cols-3 gap-4">
                      {design.existing_image && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Current 2D Image
                          </label>
                          <div className="border border-gray-300 rounded-md bg-gray-50 h-[42px] flex items-center px-2">
                            <button
                              type="button"
                              className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 p-1 rounded-md transition-all duration-200 border border-transparent hover:border-blue-200 w-full text-left"
                              onClick={(e) =>
                                handleImageClick(design.existing_image, e)
                              }
                              title="Click to view image"
                            >
                              <i className="fa fa-image text-blue-500 text-sm"></i>
                              <span className="text-sm text-blue-600 hover:text-blue-800 font-medium truncate">
                                View: {getDisplayName(design.existing_image)}
                              </span>
                              <i className="fa fa-eye text-sm text-blue-400 ml-auto"></i>
                            </button>
                          </div>
                        </div>
                      )}

                      {design.imagePreview && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            New Image Preview
                          </label>
                          <div className="border border-gray-300 rounded-md bg-gray-50 h-[42px] flex items-center px-2">
                            <button
                              type="button"
                              className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 p-1 rounded-md transition-all duration-200 border border-transparent hover:border-blue-200 w-full text-left"
                              onClick={(e) =>
                                handleImageClick(design.imagePreview, e)
                              }
                              title="Click to view preview"
                            >
                              <i className="fa fa-image text-green-500 text-sm"></i>
                              <span className="text-sm text-green-600 hover:text-green-800 font-medium">
                                Preview New Image
                              </span>
                              <i className="fa fa-eye text-sm text-green-400 ml-auto"></i>
                            </button>
                          </div>
                        </div>
                      )}

                      {design.boqName && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            BOQ File
                          </label>
                          <div className="border border-gray-300 rounded-md bg-gray-50 h-[42px] flex items-center px-2">
                            <div className="flex items-center gap-2">
                              <i className="fa fa-file-pdf text-red-500 text-sm"></i>
                              <span className="text-sm text-gray-600 truncate">
                                {design.boqName}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Current BOQ File Display */}
                  {design.existing_boq && !design.boqName && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Current BOQ File
                      </label>
                      <div className="border border-gray-300 rounded-md bg-gray-50 h-[42px] flex items-center px-2">
                        <div className="flex items-center gap-2">
                          <i className="fa fa-file-pdf text-red-500 text-sm"></i>
                          <span className="text-sm text-gray-600 truncate">
                            {design.existing_boq.split("/").pop()}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Add More Button */}
          <button
            type="button"
            onClick={handleAddDesign}
            className="flex items-center space-x-2 text-[#137e81] hover:text-[#0f6366]"
          >
            <Plus size={20} />
            <span>Add More Design</span>
          </button>
        </>
      )}

      {/* Buttons */}
      <div className="pt-4 flex gap-3 justify-end">
        {showUpdateOnly && (
          <AddButton
            text="Update"
            type="button"
            onClick={handleSubmit}
            disabled={loading}
          />
        )}
        {showSubmitAndNext && (
          <>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className={`${
                loading ? "bg-gray-400" : "bg-teal-600 hover:bg-teal-700"
              } text-white px-4 py-2 rounded-lg transition duration-200 font-medium flex items-center gap-2`}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Submitting..
                </>
              ) : (
                "Submit"
              )}
            </button>
            <button
              type="button"
              onClick={handleNext}
              disabled={!stepCompleted || loading}
              className={`
                py-[0.375rem] px-[0.75rem] rounded-lg font-[InterLight] lg:text-[15px] text-[12px] 
                bg-[#be9609] text-white w-auto opacity-95 hover:opacity-85 
                border-green flex items-center gap-2 
                ${
                  !stepCompleted || loading
                    ? "cursor-not-allowed opacity-80 bg-gray-400"
                    : ""
                }
              `}
            >
              Next
            </button>
          </>
        )}
        {showNextButton && !isEditMode && (
          <button
            type="button"
            onClick={handleNext}
            disabled={loading}
            className={`
              py-[0.375rem] px-[0.75rem] rounded-lg font-[InterLight] lg:text-[15px] text-[12px] 
              bg-[#be9609] text-white w-auto opacity-95 hover:opacity-85 
              border-green flex items-center gap-2 
              ${loading ? "cursor-not-allowed opacity-80 bg-gray-400" : ""}
            `}
          >
            Next
          </button>
        )}
      </div>

      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999]"
          onClick={closeImageModal}
          style={{ zIndex: 9999 }}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-4xl max-h-[90vh] overflow-auto relative shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4 border-b pb-3">
              <h3 className="text-xl font-semibold text-gray-700 flex items-center gap-2">
                <i className="fa fa-image text-blue-500"></i>
                Design Image Preview
              </h3>
              <button
                onClick={closeImageModal}
                className="text-gray-400 hover:text-gray-600 text-3xl font-bold leading-none hover:bg-gray-100 w-8 h-8 rounded-full flex items-center justify-center"
                title="Close"
              >
                ×
              </button>
            </div>

            <div className="text-center">
              <div className="mb-4">
                <img
                  src={
                    selectedImage.startsWith("data:")
                      ? selectedImage
                      : getImageUrl(selectedImage)
                  }
                  alt="Design Image"
                  className="max-w-full max-h-[60vh] object-contain rounded-lg border shadow-md mx-auto"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextElementSibling.style.display = "block";
                  }}
                />
                <div
                  className="hidden text-gray-500 p-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50"
                  style={{ display: "none" }}
                >
                  <i className="fa fa-exclamation-triangle text-4xl mb-4 text-yellow-500"></i>
                  <p className="text-lg font-medium mb-2">
                    Image could not be loaded
                  </p>
                  <p className="text-sm text-gray-600">
                    File: {getDisplayName(selectedImage)}
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    URL:{" "}
                    {selectedImage.startsWith("data:")
                      ? "Preview Image"
                      : getImageUrl(selectedImage)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Please check if the file exists or contact support.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t flex justify-end">
              <button
                onClick={closeImageModal}
                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureDesign;
